/** VUEX module for state management in app **/
import moment from 'moment';
import { AutodetectApi, AutodetectStatusApi } from '@/services/api-autodetect.js';

// Local state -----------------------------------------------------
export const state = getDefaultState();

// Computed properties of the state --------------------------------
export const getters = {
  bookingTypes: (state) => state.rulesets.map((ruleset) => ruleset.type),

  resultSetsByDomain: (state) => {
    let domainStatuses = [];
    //FIXME: There is a bug here. It is gathering all resultsets for a domain if it ever had that type in the domain. It should only
    // add the type if the created datetimes all match this we you'll only get the types that correspond to the latest ruleset generated
    state.rulesets.forEach((ruleset) => {
      if (Array.isArray(ruleset.domains)) {
        ruleset.domains.forEach((domain) => {
          let index = domainStatuses.findIndex((status) => status.domain === domain.name);
          if (index === -1) {
            domainStatuses.push({
              domain: domain.name,
              created: domain.created,
              types: [ruleset.type],
            });
          } else {
            domainStatuses[index].types.push(ruleset.type);
          }
        });
      }
    });
    return domainStatuses;
  },
};

// Synchronous mutation of the local state -------------------------
export const mutations = {
  mergeRulesetWithStatus(state, statuses = []) {
    state.rulesets = state.rulesets.map((ruleset) => {
      // for each rule set find the matching ruleset with timestamp
      let status = {
        ...statuses.find((status) => status.type === ruleset.type),
      };

      // for each domain check if it is outdated
      if (Array.isArray(status.domains)) {
        status.domains = status.domains.map((domain) => {
          let rulesetDate = moment(status.modified, 'YYYY-MM-DDTHH:mm:ssZ');
          let domainDate = moment(domain.created, 'YYYY-MM-DDTHH:mm:ssZ');
          if (rulesetDate.isAfter(domainDate, 'day')) {
            domain.isOutdated = true;
          }
          return domain;
        });
      }

      // merge ruleset with timetamp and return
      return { ...ruleset, ...status };
    });
  },

  setDownloads(state, downloads = []) {
    state.downloads = [...downloads];
  },

  updateDownloads(state, download) {
    // push the new one onto the
    state.downloads.unshift(download);

    // if there are more then to remove them from the end
    let extrasCount = state.downloads.length - 10;
    if (extrasCount >= 1) {
      state.downloads.splice(-1, extrasCount);
    }
  },

  resetState(state, defaultState) {
    state = defaultState;
  },
};

// Async CRUD actions against the api ------------------------------
export const actions = {
  // List of rulesets
  index({ commit }) {
    return AutodetectApi.index().then((downloads) => {
      commit('setDownloads', downloads);
    });
  },

  /**
   * Generate a new ruleset download
   * @param {Object} context - Vuex context
   * @param {Object} data - new ruleset form data
   * @returns {Promise<Array>}
   */
  store({ commit, dispatch }, { data }) {
    return AutodetectApi.store(data).then((download) => {
      dispatch('fetchStatuses');
      if (download) {
        commit('updateDownloads', download);
        return download;
      }
    });
  },

  /**
   * Fetch a list of generated rulesets
   * @param {Object} context - Vuex context
   * @returns {Promise<Array>}
   */
  fetchStatuses({ commit }) {
    return AutodetectStatusApi.index().then((statuses) => {
      commit('mergeRulesetWithStatus', statuses);
    });
  },

  /**
   * Sets the state back to the default values
   * @returns {Promise}
   */
  reset({ commit }) {
    commit('resetState', getDefaultState());
  },
};

/// ///

function getDefaultState() {
  return {
    downloads: [],
    rulesets: [
      {
        type: 'air',
        name: 'Airlines',
        example: 'United, Delta, Lufthansa',
      },
      {
        type: 'hotel',
        name: 'Hotels',
        example: 'Marriott, Hilton, Wyndham',
      },
      {
        type: 'car',
        name: 'Car Rental Agencies',
        example: 'Enterprise, Hertz, Avis',
      },
      {
        type: 'rail',
        name: 'Rail Lines',
        example: 'Amtrak, SNCF, Deutsch Bahn',
      },
      {
        type: 'transportation',
        name: 'Transportation Services',
        example: 'Uber, Lyft, Didi',
      },
      {
        type: 'activity',
        name: 'Activities',
        example: 'Ticketmaster, OpenTable',
      },
      {
        type: 'ota',
        name: 'Online Travel Agencies',
        example: 'Expedia, Priceline',
      },
      {
        type: 'tmc',
        name: 'Travel Management Companies',
        example: 'BCD Travel, Amex GBT',
      },
      {
        type: 'gds',
        name: 'GDS ',
        example: 'Amadeus, Sabre',
      },
      {
        type: 'obt',
        name: 'Online Booking Tool',
        example: 'Concur, Egencia',
      },
    ],
  };
}
